/*
 * File: AsanaTaskCard.tsx
 * Project: a-a
 * File Created: Wednesday, 7th May 2025 5:40:47 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Wednesday, 14th May 2025 6:53:28 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import { Box, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import React from 'react';
import zeplinColor from '../../../../theme/Color';
import { useDraggable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const AsanaTaskCard = ({
  item,
  loading,
  id,
}: {
  item: {
    assignee: string;
    assigneePhoto: string;
    dueDate: string;
    id: string;
    priority: string;
    priorityColor: string;
    status: string;
    statusColor: string;
    title: string;
  };
  loading: boolean;
  id: string;
}) => {
  const theme = useTheme();
  // const { attributes, listeners, setNodeRef } = useDraggable({
  //   id: id,
  // });
  // console.log(attributes, listeners, setNodeRef);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      sx={{
        borderRadius: '16px',
        padding: '16px',
        backgroundColor: zeplinColor.Neutral1,
        border: `1px solid ${zeplinColor.Neutral5}`,
        // width: '195.5px',
        // height: '156px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        // justifyContent: 'center',
        // alignItems: 'flex-start',
        //   alignItems: 'center',
        // flex: 1,
        gap: '24px',
        justifyContent: 'space-between',
        cursor: 'grab',
      }}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      //   onClick={(e: any) => handleNavigate(e)}
    >
      {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}> */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
        {loading ? (
          <Skeleton
            variant='text'
            sx={{
              fontSize: theme.typography.body2,
              width: '100%',
              // Optionally adjust other styles here
            }}
          />
        ) : (
          <Typography variant='body2'>{item?.title}</Typography>
        )}
        <Box sx={{ display: 'flex', gap: '8px' }}>
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
                // Optionally adjust other styles here
              }}
            />
          ) : (
            item?.priority && (
              <Chip
                label={<Typography variant='label1'>{item?.priority}</Typography>}
                sx={{
                  bgcolor: item?.priorityColor,
                  color: zeplinColor.Neutral1,
                  // width: '39px',
                  '&.MuiChip-root': {
                    height: '22px',
                    padding: '4px 8px',
                    borderRadius: '50px',
                  },
                  '& .MuiChip-label': {
                    padding: 0,
                  },
                }}
              />
            )
          )}
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
                // Optionally adjust other styles here
              }}
            />
          ) : (
            item?.status && (
              <Chip
                label={<Typography variant='label1'>{item?.status}</Typography>}
                sx={{
                  bgcolor: item?.status?.toLocaleLowerCase().includes('at risk')
                    ? zeplinColor.Error
                    : zeplinColor.Success, //item?.statusColor,
                  color: zeplinColor.Neutral1,
                  // width: '39px',
                  '&.MuiChip-root': {
                    height: '22px',
                    padding: '4px 8px',
                    borderRadius: '50px',
                  },
                  '& .MuiChip-label': {
                    padding: 0,
                  },
                }}
              />
            )
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {loading ? (
          <Skeleton
            variant='text'
            sx={{
              fontSize: theme.typography.body2,
              width: '100%',
              // Optionally adjust other styles here
            }}
          />
        ) : !item?.assignee ? (
          <img
            src={'/assets/no-assignee.png'}
            width={'26px'}
            height={'26px'}
            style={{ borderRadius: '100px', objectFit: 'cover' }}
            alt='no-assignee-photo'
          />
        ) : (
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Box
              sx={{
                borderRadius: '100px',
                padding: '10px',
                width: '26px',
                height: '26px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: zeplinColor.Brand1,
              }}
            >
              {item?.assigneePhoto ? (
                <img
                  src={item?.assigneePhoto}
                  width={'26px'}
                  height={'26px'}
                  style={{ borderRadius: '100px', objectFit: 'cover' }}
                  alt='asana-task-assignee-photo'
                />
              ) : (
                <Typography variant='label1' color={zeplinColor.Neutral1}>
                  {item?.assignee
                    ?.split(' ')
                    .map((value: any, index: number) =>
                      index === 0 || index === item?.assignee?.split(' ').length - 1
                        ? value[0]
                        : '',
                    )}
                </Typography>
              )}
            </Box>

            <Typography variant='body2'>{item?.assignee}</Typography>
          </Box>
        )}
        {loading ? (
          <Skeleton
            variant='text'
            sx={{
              fontSize: theme.typography.body2,
              width: '100%',
              // Optionally adjust other styles here
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <img src='/assets/calendar.svg' width={18} height={18} style={{}} />
            <Typography
              variant='body3'
              color={
                new Date(item?.dueDate) < new Date() ? zeplinColor.Error : zeplinColor.BasicBlack
              }
            >
              {new Date(item?.dueDate)
                ? new Date(item?.dueDate)
                    .toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })
                    .replace(/(\s)(\w{3})(\s)(\d{4})/, '$1$2, $4')
                : ''}
            </Typography>
            {new Date(item?.dueDate) < new Date() && (
              <img src='/assets/overdue-icon.svg' width={18} height={18} style={{}} />
            )}
          </Box>
        )}
      </Box>
      {/* </Box> */}
    </Box>
  );
};

export default AsanaTaskCard;
